import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DoCheck, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IRequestCallback } from '@df/api/request/request.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { NCheckout } from '@df/checkout/df-checkout.config';
import { App } from '@df/core/app';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { ZippyService } from '@df/legacy/ui/zippy/zippy.service';
import { ImageModule } from '@df/module-image/image.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { SampleasNexusApiOverrideService } from '@df/samples/samples-nexus-api-override.service';
import { NSamples } from '@df/samples/samples.namespace';
import { Basket } from '@df/session/api/basket/basket';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subject } from 'rxjs';
import { ProductStockDescriptionService } from '../../catalog/product/stock-levels/product-stock-description.service';
import { CheckoutAccountAlternativeComponent } from '../account/checkout-account-alternative.component';
import { CheckoutAccountComponent } from '../account/checkout-account.component';
import { CheckoutBasketSummaryComponent } from '../basket-summary/checkout-basket-summary.component';
import { CheckoutConfirmationComponent } from '../confirmation/checkout-confirmation.component';
import { CheckoutDeliverySelectionComponent } from '../delivery-selection/checkout-delivery-selection.component';
import { CheckoutDeliveryComponent } from '../delivery/checkout-delivery.component';
import { CheckoutNavigationComponent } from '../navigation/checkout-navigation.component';
import { CheckoutPaymentComponent } from '../payment/checkout-payment.component';
import { CheckoutStepComponent } from '../step/checkout-step.component';
import { NCheckoutFormCms } from './checkout-form-cms.component';

const zippyGroup = 'checkout';
export enum CheckoutDeliveryType {
    collection = 'collection',
    shipping = 'shipping'
}

@Component({
    selector: 'checkout-form',
    templateUrl: 'checkout-form.html',
    styleUrls: ['checkout-form.scss'],
    providers: [{ provide: CheckoutFormComponentDef, useExisting: CheckoutFormComponent }],
    standalone: true,
    imports: [
        CommonModule,
        ImageModule,
        CheckoutStepComponent,
        CheckoutConfirmationComponent,
        CheckoutPaymentComponent,
        CheckoutDeliverySelectionComponent,
        CheckoutAccountAlternativeComponent,
        CheckoutDeliveryComponent,
        CheckoutAccountComponent,
        CheckoutBasketSummaryComponent,
        CheckoutNavigationComponent,
        IfSizeDirective,
        ICmsClientModule
    ]
})
export class CheckoutFormComponent extends CheckoutFormComponentDef implements OnInit, OnDestroy, DoCheck {
    beforeSubmitSubject = new Subject<void>();
    checkoutFormInitiated = false;
    stepNames = Checkout.EStepName;
    @Input()
    content?: NCheckoutFormCms.IBlockDataContent;
    override visibleStepsSequence: CheckoutStepComponent[] = [];
    private _deliveryType?: CheckoutDeliveryType;
    paymentStepName = Checkout.EStepName.payment;
    deliveryStepName = Checkout.EStepName.delivery;
    accountStepName = Checkout.EStepName.account;
    deliverySelectionStepName = 'deliverySelection' as Checkout.EStepName;

    constructor(
        activatedRoute: ActivatedRoute,
        protected override _ref: ChangeDetectorRef,
        protected override timeoutService: TimeoutService,
        override readonly app: App,
        protected override scrollToService: ScrollToService,
        @Inject(NCheckout.OPTIONS) moduleConfig: NCheckout.IModuleOptions,
        private zippyService: ZippyService,
        private sessionStorageService: SessionStorageService,
        sampleasNexusApiOverrideService: SampleasNexusApiOverrideService,
        private productStockDescriptionService: ProductStockDescriptionService
    ) {
        super(activatedRoute, _ref, timeoutService, app, scrollToService, moduleConfig);
        if (this.isSamples) {
            this.nexusApiService = sampleasNexusApiOverrideService;
            this.resetShippingMethodPath += NSamples.SAMPLE_QUOTE_SUFFIX;
        }
    }
    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.events.on(Checkout.EVENTS.SubmitBefore).subscribe(() => this.beforeSubmitSubject.next()));
        this.subscriptions.add(this.customer.model.obs.data.subscribe(() => this.detectChanges()));
        this._deliveryType = this.sessionStorageService.get('deliveryType');
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.ShippingMethodsUpdate).subscribe(() => this.onShippingMethodsUpdate()));
        this.onShippingMethodsUpdate();

        if (this.deliveryType === CheckoutDeliveryType.shipping && this.selfCollect) {
            this.selectShipping();
        } else if (this.deliveryType === CheckoutDeliveryType.collection && !this.selfCollect) {
            this.selectCollection();
        }
        if (!this.data.custom) {
            this.data.custom = {};
        }
        delete this.data?.newsletter;
        this.checkoutFormInitiated = true;
    }
    override redirectToSuccess() {
        if (this.isSamples) {
            this.router.navigate([this.customer.samples.config.checkoutSuccesUrl]);
        } else {
            super.redirectToSuccess();
        }
    }
    override get lastStepName(): string | undefined {
        if (this.isSamples) {
            return undefined;
        }
        return this.checkout.lastStepName;
    }
    override set lastStepName(value: string | undefined) {
        if (!this.isSamples) {
            this.checkout.lastStepName = value;
        }
    }
    /**
     * Advance to the next step after given one
     * Closes all zippies
     */
    override goToNextStep(vsStep?: Checkout.IStep | string) {
        super.goToNextStep(vsStep);
        this.zippyService.closeAll(zippyGroup);
    }
    override goToStep(step?: string | Checkout.IStep): void {
        const stepBeforeChange = this.currentStep;
        super.goToStep(step);
        if (stepBeforeChange?.name === this.currentStep?.name) {
            this.scrollToStepSection();
        }
    }
    ngDoCheck() {
        // make sure that any changes didnt make prev steps invalid
        if (!this._confirmationLock && this.currentStepName) {
            this.checkStep(this.currentStep);
        }
    }
    /**
     * Customer basket shorthand
     */
    override get basket(): Basket {
        return this.isSamples ? this.app.customer.samplesBasket : this.app.customer.basket;
    }
    get isSamples(): boolean {
        return !!this.backendData?.isSamples;
    }
    /**
     * Intergration with zippies
     */
    getExpandableId(stepName: string): string[] {
        return [zippyGroup, stepName];
    }
    /**
     * Logic for when zippy is used in step
     */
    isExpandableOff(stepName: string): boolean {
        return this.getStepIndex(stepName) === this.currentStepIndex;
    }
    /**
     * Guest checkout toggle method
     */
    override toggleGuestCheckout(toState: boolean): void {
        super.toggleGuestCheckout(toState);
        this.detectChanges();
    }
    protected override getScrollToCurrentStepDelay(): number {
        return 450;
    }
    override getSubmissionData(): Checkout.ISubmissionData {
        return { ...super.getSubmissionData(), subscriberProperties: this.data.subscriberProperties };
    }
    /////////////////////////////////////////////////
    /////
    ///// Delivery type
    /////
    /////////////////////////////////////////////////
    /**
     * Returns if we should display delivery selection -> both types available
     */
    get displayDeliverySelection(): boolean {
        return this.hasCollectionMethods;
    }
    /**
     * Checks if delivery is selected
     * @returns {boolean}
     */
    get isShipping(): boolean {
        return this.deliveryType === CheckoutDeliveryType.shipping;
    }
    /**
     * Checks if click and collect is selected
     * @returns {boolean}
     */
    get isCollection(): boolean {
        return this.deliveryType === CheckoutDeliveryType.collection;
    }
    get deliveryTypeSelected(): CheckoutDeliveryType | undefined {
        return this.deliveryType;
    }
    /**
     * Sets delivery as selected delivery type
     */
    selectShipping(): void {
        this.selectDeliveryType(CheckoutDeliveryType.shipping);
    }
    /**
     * Sets click and collect as selected delivery type
     */
    selectCollection(): void {
        this.selectDeliveryType(CheckoutDeliveryType.collection);
    }
    toggleDeliverySelection(): void {
        const type = this.isCollection ? CheckoutDeliveryType.shipping : CheckoutDeliveryType.collection;
        this.resetShippingMethod(
            true,
            () => this.selectDeliveryType(type),
            () => this.selectDeliveryType(type)
        );

        this.detectChanges();
    }
    /**
     * Returns selected delivery type
     */
    protected set deliveryType(type: CheckoutDeliveryType | undefined) {
        this._deliveryType = type;
        this.sessionStorageService.set('deliveryType', type);
    }
    /**
     * Sets selected delivery type
     */
    protected get deliveryType(): CheckoutDeliveryType | undefined {
        return this._deliveryType;
    }
    /**
     * Clears shipping additional data
     */
    clearShippingAdditionalData(): void {
        if (this.data.shipping.additionalData) {
            this.data.shipping.additionalData = {
                store_id: null
            };
        }
    }
    /**
     * Select delivery type
     * @param {CheckoutDeliveryType} type
     */
    selectDeliveryType(type: CheckoutDeliveryType | undefined, nextStep = false): void {
        this.deliveryType = type;
        if (type === CheckoutDeliveryType.collection) {
            if(!this.selfCollect){
                this.selfCollect = true;
                this.data.billing?.to_shipping && (this.data.billing.to_shipping = false)
                this.saveShippingMethod();
            }
        } else {
            if (this.selfCollect) {
                this.clearShippingAdditionalData();
            }
            this.selfCollect = false;
            // if (!this.data.shipping.method) {
            //     const methodCode = this.data.shipping.method || this.nonCollectionShippingMethods?.[0]?.code;
            //     if (methodCode) {
            //         this.data.shipping.method = methodCode;
            //         this.saveShippingMethod();
            //     }
            // }
        }
        if (nextStep) {
            this.timeoutService.setTimeout(() => {
                this.goToNextStep();
                this.detectChanges();
            }, 100);
        }
        this.detectChanges();
    }
    /**
     * Updated delivery type
     * if click and collect selected but no method is available
     * we have to unselect it
     */
    updateDeliveryType(): void {
        if (!this.hasCollectionMethods) {
            if (this.selfCollect) {
                this.selfCollect = false;
                this.clearShippingAdditionalData();
            }
            if (!this.isShipping) {
                this.selectShipping();
            }
        }
        if (this.data?.shipping?.method) {
            if (this.selfCollect && this.hasCollectionMethods) {
                this.selectCollection();
            } else if (!this.isShipping) {
                this.selectShipping();
            }
        }
        this.detectChanges();
    }
    /////////////////////////////////////////////////
    /////
    ///// Shipping methods
    /////
    /////////////////////////////////////////////////
    /**
     * On shipping methods update callback
     */
    protected onShippingMethodsUpdate(): void {
        this.updateDeliveryType();
    }

    override _saveBillingAddress(force = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        this.productStockDescriptionService.saveBasketLeadTimes();
        return super._saveBillingAddress(force, onSuccess, onError);
    }
}
